import React, { useCallback, useState, useEffect } from 'react';
import { render } from 'react-dom';
import createApp from '@shopify/app-bridge';
import { authenticatedFetch } from '@shopify/app-bridge/utilities';
import { AppLink, NavigationMenu, Redirect } from '@shopify/app-bridge/actions';
import Main from './Main';
import { I18nContext, I18nManager, useI18n } from '@shopify/react-i18n';
import localeCode from 'locale-code';
import enTranslations from '@shopify/polaris/locales/en.json';
import {
	AppProvider,
	Frame,
	Toast,
	TopBar,
	Navigation,
} from '@shopify/polaris';
import { HomeIcon } from '@shopify/polaris-icons';
import { LiveChatLoaderProvider, HelpScout } from 'react-live-chat-loader';
import { BrowserRouter, useNavigate, useLocation } from 'react-router-dom';

const App = ({ props, i18nManager }) => {
	const { user, defaults } = props;
	const [i18n, ShareTranslations] = useI18n();
	const navigate = useNavigate();
	const { pathname } = useLocation();

	const [polarisI18n] = useI18n({
		id: 'Polaris',
		fallback: enTranslations,
		async translations(locale) {
			const dictionary = await import(
				/* webpackChunkName: "Polaris-i18n", webpackMode: "lazy-once" */ `@shopify/polaris/locales/${locale}.json`
			);
			return dictionary?.default;
		},
	});

	const setLocale = useCallback((locale) => {
		try {
			localStorage.setItem('locale', locale);
		} catch {}
		i18nManager.update({ ...i18nManager.details, locale });
	}, []);

	useEffect(() => {
		if (!app) return;

		var unsubscribe = app.subscribe(Redirect.Action.APP, ({ path }) => {
			const url = path.replace('/Dashboard', '');
			navigate(`${url ? url : '/'}${location.search}`);
		});

		return () => {
			unsubscribe();
		};
	}, []);

	const fetchWithToken = (uri, options) => {
		if (options === void 0) {
			options = {};
		}

		const headers = new Headers(options.headers);
		headers.append('Authorization', 'Bearer ' + token);
		headers.append('X-Requested-With', 'XMLHttpRequest');
		const finalHeaders = {};
		headers.forEach(function (value, key) {
			finalHeaders[key] = value;
		});
		options.headers = finalHeaders;

		return fetch(uri, options);
	};

	const fetchOpt = app ? authenticatedFetch(app) : fetchWithToken;

	const redirectOpt = (
		url,
		newContext = false,
		action = Redirect.Action.REMOTE
	) => {
		if (app) {
			if (action == Redirect.Action.ADMIN_PATH) {
				redirect.dispatch(action, {
					path: url,
					newContext: newContext,
				});
				return;
			} else if (action == Redirect.Action.REMOTE) {
				redirect.dispatch(action, {
					url: url,
					newContext: newContext,
				});
				return;
			}
		}

		if (action == Redirect.Action.ADMIN_PATH) {
			url = `https://${user.shopifyDomain}/admin${url}`;
		}
		if (newContext) window.open(url, '_blank');
		else window.location.assign(url);
	};

	// Toast message
	const [toastProps, setToastProps] = useState(null);
	const [toastActive, setToastActive] = useState(false);
	const toggleToastActive = useCallback(
		() => setToastActive((active) => !active),
		[]
	);

	const toastMarkup = toastActive && (
		<Toast {...toastProps} onDismiss={toggleToastActive} />
	);

	const showToast = ({ content, action, error }) => {
		setToastProps({
			content,
			action,
			error,
			duration: action ? 10000 : undefined,
		});
		setToastActive(true);
	};

	const fetchJson = async (
		url,
		method,
		body,
		signal,
		successCallback,
		finallyCallback,
		errorCallback
	) => {
		let success = false;

		try {
			const resp = await fetchOpt(url, {
				signal,
				method,
				headers: {
					'Content-Type': 'application/json',
				},
				body,
			});

			if (resp.ok) {
				const json = await resp.json();
				if (json.success) {
					success = true;
					if (successCallback) {
						successCallback(json.data);
					}
				} else {
					if (errorCallback) {
						errorCallback(json.errors[0]);
					} else {
						showToast({
							content: json.errors[0],
							error: true,
						});
					}
				}
			} else {
				showToast({
					content: i18n.translate('Common.ErrorMsg', {
						error: resp.status,
					}),
					error: true,
				});
			}
		} catch (error) {
			if (error.name != 'AbortError') throw error;
		} finally {
			if (finallyCallback) finallyCallback();
		}

		return success;
	};

	const [mobileNavigationActive, setMobileNavigationActive] = useState(false);

	const toggleMobileNavigationActive = useCallback(
		() =>
			setMobileNavigationActive(
				(mobileNavigationActive) => !mobileNavigationActive
			),
		[]
	);

	const shared = {
		app,
		host,
		token,
		fetchOpt,
		fetchJson,
		redirectOpt,
		showToast,
		setLocale,
		SHOPIFY_SITEMAPPER_APP_BLOCK_ID: 'f8fe1657-b243-4261-8f0f-cb2fd9da6ce0',
		...props,
	};

	// Update Shopify navigation menu
	const [navigationItems, setNavigationItems] = useState([]);

	if (app) {
		useEffect(() => {
			const htmlLink = AppLink.create(app, {
				label: i18n.translate('Common.HtmlSitemap'),
				destination: '/html',
			});

			const xmlLink = AppLink.create(app, {
				label: i18n.translate('Common.XmlSitemap'),
				destination: '/xml',
			});

			const seoLink = AppLink.create(app, {
				label: i18n.translate('Main.SeoTool.plural'),
				destination: '/seo',
			});

			const integrationLink = AppLink.create(app, {
				label: i18n.translate('Main.Integration.plural'),
				destination: '/integration',
			});

			setNavigationItems([htmlLink, xmlLink, seoLink, integrationLink]);
		}, [i18n.language]);

		useEffect(() => {
			if (navigationItems.length == 0) return;

			const activeLink = navigationItems.find(
				(i) => i.destination == pathname
			);
			NavigationMenu.create(app, {
				items: navigationItems,
				active: activeLink,
			});
		}, [pathname, navigationItems]);
	}

	return (
		<AppProvider i18n={polarisI18n.translations}>
			<ShareTranslations>
				<Frame
					navigation={
						!app && (
							<Navigation>
								<Navigation.Section
									items={[
										{
											label: i18n.translate('Main.Title'),
											icon: HomeIcon,
											onClick: () =>
												navigate(`/${location.search}`),
											selected: pathname == '/',
										},
										{
											label: i18n.translate(
												'Common.HtmlSitemap'
											),
											onClick: () =>
												navigate(
													`/html${location.search}`
												),
											selected: pathname == '/html',
										},
										{
											label: i18n.translate(
												'Common.XmlSitemap'
											),
											onClick: () =>
												navigate(
													`/xml${location.search}`
												),
											selected: pathname == '/xml',
										},
										{
											label: i18n.translate(
												'Main.SeoTool.plural'
											),
											onClick: () =>
												navigate(
													`/seo${location.search}`
												),
											selected: pathname == '/seo',
										},
										{
											label: i18n.translate(
												'Main.Integration.plural'
											),
											onClick: () =>
												navigate(
													`/integration${location.search}`
												),
											selected:
												pathname == '/integration',
										},
										{
											label: i18n.translate(
												'Main.FeatureRequest.plural'
											),
											onClick: () =>
												navigate(
													`/features${location.search}`
												),
											selected: pathname == '/features',
										},
									]}
								></Navigation.Section>
							</Navigation>
						)
					}
					topBar={
						!app && (
							<TopBar
								showNavigationToggle
								onNavigationToggle={
									toggleMobileNavigationActive
								}
								userMenu={
									<TopBar.UserMenu
										name={user.name}
										detail={user.shopifyDomain}
										avatar={
											user.isDemo
												? undefined
												: `https://gravatar.com/avatar/${user.emailHash}`
										}
										initials={
											user.isDemo ? 'DM' : undefined
										}
									/>
								}
							/>
						)
					}
					showMobileNavigation={mobileNavigationActive}
					onNavigationDismiss={toggleMobileNavigationActive}
					logo={{
						topBarSource: `${defaults.homeUrl}/img/logo_32.png`,
						width: 32,
						url: defaults.homeUrl,
						accessibilityLabel: 'Sitemapper',
					}}
				>
					<Main shared={shared} />
					{toastMarkup}
					<HelpScout color='#394956' />
				</Frame>
			</ShareTranslations>
		</AppProvider>
	);
};

const params = new URLSearchParams(location.search);
const host = params.get('host');
const token = params.get('token');
const app = token
	? undefined
	: createApp({ apiKey: window.apiKey, host, forceRedirect: true });
const redirect = app ? Redirect.create(app) : undefined;

let locale;
try {
	locale = localStorage.getItem('locale') ?? navigator.language;
} catch {
	locale = navigator.language;
}

const i18nManager = new I18nManager({
	locale: localeCode.validateLanguageCode(locale) ? locale : 'en-US',
	fallbackLocale: 'en-US',
	currency: 'USD',
	// pseudolocalize: DEVELOPMENT,
	onError(error) {
		console.error(error);
	},
});

render(
	<BrowserRouter basename='/Dashboard'>
		<I18nContext.Provider value={i18nManager}>
			<LiveChatLoaderProvider
				providerKey='c7d8122c-98cd-4d18-9e03-7978fe0ddee7'
				provider='helpScout'
			>
				<App i18nManager={i18nManager} props={window.props} />
			</LiveChatLoaderProvider>
		</I18nContext.Provider>
	</BrowserRouter>,
	document.getElementById('app')
);
